import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { environment } from '../../../environments/environment';

interface EmbeddedProps {
  horizontalAlignment: string;
  round: string;
  isShadow: boolean;
  link: string;
  code: string;
  active: string;
  width: number;
  height: number;
  editorPlugins: Record<string, any>;
}

interface EmbeddedStyledProps {
  horizontalAlignment: string;
  round: string;
  shadow: boolean;
  width: number;
  height: number;
}

const EmbeddedStyled = styled.div<EmbeddedStyledProps>`
  position: relative;
  display: flex;
  justify-content: ${(props) => props.horizontalAlignment};
  .iframe__wrapper {
    overflow: hidden;
    width: ${(props) => `${props.width}%`};
    height: ${(props) => `${props.height}px`};
    border-radius: ${(props) => props.round};
    box-shadow: ${(props) => (props.shadow ? 'rgba(0, 0, 0, 0.15) 0px 4px 8px' : 'none')};
  }
  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
  .code {
    width: 100%;
  }
  .placeholder {
    background: rgba(243, 242, 245, 0.5);
    border: 1px dashed #d0cdd8;
    border-radius: 8px;
    padding: 8px;
    font-family: 'Inter';
    font-weight: 600;
    font-size: 11px;
    line-height: 14px;
    color: #71688b;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${(props) => props.round};
    img {
      margin-right: 8px;
    }
  }
  .empty {
    padding: 50px 0;
    height: 100%;
    background: rgba(18, 3, 62, 0.03);
    border: 1px dashed rgba(18, 3, 62, 0.15);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: ${(props) => props.round};
    .icon {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: rgba(18, 3, 62, 0.05);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;
      flex-shrink: 0;
    }
    p {
      margin: 0;
      font-family: 'Inter';
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      color: #71688b;
      text-align: center;
    }
  }
`;

const Embedded: FC<EmbeddedProps> = ({ ...props }) => {
  const regex =
    /((https?:\/\/)?(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;
  const code = useRef<HTMLDivElement>(null);
  const [isCode, setIsCode] = useState(false);
  const isEditor: boolean = !!props.editorPlugins;
  const ResizeAxis = props.editorPlugins?.ResizeAxis;
  const assets: string = environment().assetsLink;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<string>(String(props.width) || '100');
  const [height, setHeight] = useState<number>(props.height);
  const [elementDomWidth, setElementDomWidth] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  useEffect(() => {
    let fragment: DocumentFragment | string = '';
    try {
      fragment = document.createRange().createContextualFragment(props.code);
    } catch (e) {}
    if (!code || !code.current) return;
    code.current.innerHTML = '';
    code.current.append(fragment);
    setIsCode(code.current.scrollHeight === 0);
  }, [props.code, props.active]);

  useEffect(() => {
    if (containerRef && containerRef.current) setElementDomWidth(containerRef.current.clientWidth);
    if (containerRef && containerRef.current) {
      const chart: HTMLDivElement | null = containerRef.current.querySelector('.iframe__wrapper');
      if (!chart) return;
      setContainerWidth(chart.clientWidth);
      setContainerHeight(chart.clientHeight);
    }
  }, [width, height]);

  useEffect(() => {
    setWidth(String(props.width));
    setHeight(props.height);
  }, [props.width, props.height]);

  const emitWidth = (width: string): void => setWidth(width);
  const emitHeight = (height: number): void => setHeight(height);

  return (
    <EmbeddedStyled
      horizontalAlignment={props.horizontalAlignment}
      round={props.round}
      shadow={props.isShadow}
      width={Number(width)}
      height={height}
      ref={containerRef}>
      {ResizeAxis && (
        <ResizeAxis
          widthProps={{
            width,
            property: 'width',
            min: 1,
            max: 100,
            elementDomWidth,
            containerWidth,
            emitWidth,
          }}
          heightProps={{ height, property: 'height', containerHeight, emitHeight }}
        />
      )}
      <div className="iframe__wrapper">
        {props.active === 'link' && regex.test(props.link) && (
          <iframe
            src={props.link.startsWith('https://') ? props.link : `https://${props.link}`}
            title="URL link"></iframe>
        )}
        {props.active === 'code' && props.code && <div className="code" ref={code}></div>}
        {isEditor && (
          <>
            {isCode && props.code && props.active === 'code' && (
              <div className="placeholder">
                <img
                  src={`${assets}/assets/images/placeholder-code.svg`}
                  alt=""
                  width="16"
                  height="16"
                />
                Embedded code
              </div>
            )}
            {((props.active === 'code' && !props.code) ||
              (props.active === 'link' && !props.link)) && (
              <div className="empty">
                <div className="icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_5977_379051)">
                      <path
                        d="M24 12.0015L18.343 17.6585L16.929 16.2445L21.172 12.0015L16.929 7.75846L18.343 6.34446L24 12.0015ZM2.828 12.0015L7.071 16.2445L5.657 17.6585L0 12.0015L5.657 6.34446L7.07 7.75846L2.828 12.0015ZM9.788 21.0015H7.66L14.212 3.00146H16.34L9.788 21.0015Z"
                        fill="#71688B"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_5977_379051">
                        <rect width="24" height="24" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <p>Add your HTML here</p>
              </div>
            )}
          </>
        )}
      </div>
    </EmbeddedStyled>
  );
};

const environmentLink: string = environment().assetsLink;
const settings: any = [
  [
    {
      settingName: 'HorizontalAlignment',
    },
    {
      name: 'EmbeddedHTML',
      title: 'Embedded',
      isShowTitle: false,
      icon: environmentLink + '/assets/images/Options/code.svg',
      hasDrop: true,
      dropWidth: 'big',
      controls: [
        {
          controlName: 'EmbeddedHTML',
        },
      ],
    },
    {
      settingName: 'Size',
      removePadding: true,
      dropWidth: 'big',
      controls: [
        {
          controlName: 'Slider',
          title: 'Size',
          property: 'width',
          defaultValue: 100,
          suffix: '%',
          min: 1,
          separateLine: true,
          padding: '16px',
        },
        {
          controlName: 'ImageSelect',
          property: 'height',
          title: 'Height',
          defaultValue: 'M',
          padding: '0 16px 16px',
          isValuesInsideBox: true,
          useTitle: true,
          presetData: [
            {
              title: 'XS',
              value: 256,
              width: 20,
            },
            {
              title: 'S',
              value: 368,
              width: 20,
            },
            {
              title: 'M',
              value: 488,
              width: 20,
            },
            {
              title: 'L',
              value: 720,
              width: 20,
            },
            {
              title: 'XL',
              value: 960,
              width: 20,
            },
          ],
        },
      ],
    },
    {
      settingName: 'BorderRadius',
    },
    {
      settingName: 'Shadow',
    },
  ],
  [
    {
      settingName: 'Anchor',
    },
  ],
];

const options = [
  {
    name: 'code',
    title: 'Edit code',
    icon: environmentLink + '/assets/images/linkOption.svg',
    settingTitle: 'Embedded',
    action: 'setActiveSetting',
  },
];

export default {
  Embedded,
  settings,
  options,
  label: 'Embedded HTML',
};
